'use client';

import { PageHeader } from '@/shared/layout';
import { Building2, Users, Shield, Award, Target, Eye, CalendarCheck, Globe, type LucideIcon } from 'lucide-react';
import { usePageContent } from '@/core/i18n/content';
import { ABOUT_CONTENT } from '@/core/i18n/content/about.content';

/* -------------------------------------------------------------------------- */
/*  Icon maps                                                                 */
/* -------------------------------------------------------------------------- */

const STAT_ICONS: LucideIcon[] = [Building2, CalendarCheck, Globe, Users];
const VALUE_ICONS: LucideIcon[] = [Award, Shield, Users, Building2];

/* -------------------------------------------------------------------------- */
/*  AboutPage                                                                 */
/* -------------------------------------------------------------------------- */

export function AboutPage() {
  const c = usePageContent(ABOUT_CONTENT);

  return (
    <div className="min-h-screen bg-hms-bg">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <PageHeader title={c.header.title} description={c.header.description} />

        {/* Mission & Vision */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <section className="rounded-xl border border-hms-border bg-hms-card p-6 space-y-3">
            <h2 className="text-base font-semibold text-hms-text flex items-center gap-2">
              <Target className="h-4 w-4 text-hms-gold" />
              {c.mission.title}
            </h2>
            <p className="text-sm text-hms-textSub leading-relaxed">{c.mission.description}</p>
          </section>
          <section className="rounded-xl border border-hms-border bg-hms-card p-6 space-y-3">
            <h2 className="text-base font-semibold text-hms-text flex items-center gap-2">
              <Eye className="h-4 w-4 text-hms-gold" />
              {c.vision.title}
            </h2>
            <p className="text-sm text-hms-textSub leading-relaxed">{c.vision.description}</p>
          </section>
        </div>

        {/* Key statistics */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          {c.stats.map((stat, idx) => {
            const Icon = STAT_ICONS[idx] ?? Building2;
            return (
              <div key={idx} className="rounded-xl border border-hms-border bg-hms-card p-5 text-center space-y-2">
                <Icon className="h-5 w-5 text-hms-gold mx-auto" />
                <p className="text-2xl font-bold text-hms-text tracking-tight">{stat.value}</p>
                <p className="text-xs text-hms-textSub">{stat.label}</p>
              </div>
            );
          })}
        </div>

        {/* Core values */}
        <h2 className="mt-12 text-lg font-semibold text-hms-text">{c.valuesTitle}</h2>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-5">
          {c.values.map((v, idx) => {
            const Icon = VALUE_ICONS[idx] ?? Award;
            return (
              <section key={idx} className="rounded-xl border border-hms-border bg-hms-card p-6 space-y-3">
                <h3 className="text-base font-semibold text-hms-text flex items-center gap-2">
                  <Icon className="h-4 w-4 text-hms-gold" />
                  {v.title}
                </h3>
                <p className="text-sm text-hms-textSub leading-relaxed">{v.description}</p>
              </section>
            );
          })}
        </div>

        {/* Company story */}
        <section className="mt-8 rounded-xl border border-hms-border bg-hms-card p-6 space-y-3">
          <h2 className="text-base font-semibold text-hms-text flex items-center gap-2">
            <Building2 className="h-4 w-4 text-hms-gold" />
            {c.story.title}
          </h2>
          {c.story.paragraphs.map((p, idx) => (
            <p key={idx} className="text-sm text-hms-textSub leading-relaxed">{p}</p>
          ))}
        </section>
      </div>
    </div>
  );
}
